import { importGapsApi } from '@/lib/api';
import type { ImportGap } from './types';

/**
 * Report import gaps to the backend for roadmap tracking
 *
 * This service sends unsupported node types and parameters to the backend
 * so we can prioritize which features to build next based on actual usage.
 */
export async function reportImportGaps(
  sessionId: string,
  workflowId: string | null,
  gaps: ImportGap[]
): Promise<void> {
  // Don't send empty reports
  if (gaps.length === 0) {
    console.log('[n8n-import] No gaps to report');
    return;
  }

  try {
    // Transform gaps to API format
    const apiGaps = gaps.map(gap => ({
      gapType: gap.gapType,
      sourceNodeType: gap.sourceNodeType,
      sourceNodeName: gap.sourceNodeName,
      parameterName: gap.parameterName,
      parameterValue: gap.parameterValue ? JSON.stringify(gap.parameterValue) : undefined,
      description: gap.description,
      originalConfig: JSON.stringify(gap.originalConfig),
      placeholderNodeId: gap.placeholderNodeId,
    }));

    await importGapsApi.report(sessionId, workflowId, apiGaps);

    console.log(`[n8n-import] Reported ${gaps.length} gaps to backend for session ${sessionId}`);
  } catch (error) {
    // Log but don't throw - gap reporting should not block import
    console.error('[n8n-import] Failed to report import gaps:', error);
  }
}

/**
 * Get import gap statistics for admin dashboard
 */
export async function getImportGapStats(days: number = 30) {
  try {
    const response = await importGapsApi.getStats(days);
    return response.data;
  } catch (error) {
    console.error('[n8n-import] Failed to fetch gap stats:', error);
    throw error;
  }
}

/**
 * Get top requested unsupported nodes for roadmap planning
 */
export async function getRoadmapSuggestions(limit: number = 20, days: number = 90) {
  try {
    const response = await importGapsApi.getRoadmapSuggestions(limit, days);
    return response.data;
  } catch (error) {
    console.error('[n8n-import] Failed to fetch roadmap suggestions:', error);
    throw error;
  }
}

/**
 * Get top requested unsupported parameters for a specific node type
 */
export async function getParameterGaps(nodeType: string, limit: number = 20, days: number = 90) {
  try {
    const response = await importGapsApi.getParameterGaps(nodeType, limit, days);
    return response.data;
  } catch (error) {
    console.error('[n8n-import] Failed to fetch parameter gaps:', error);
    throw error;
  }
}
